import React, { FC } from 'react';
import {
	Avatar,
	Badge,
	Box,
	ClickAwayListener,
	Grow,
	IconButton,
	List,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
	Paper,
	Popper,
	Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import moment from 'moment';
import { UserContext } from '../../../../provider/user';
import { AppColor, UserNotificationModel } from '../../../../config/types';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { NotificationContext } from '../../../../reducer/notification';
import { db } from '../../../../config/firebase';
import { collection, doc, limit, orderBy, query, updateDoc } from 'firebase/firestore';
import { userNotificationConverter } from '../../../../utils/firestoreDataConverters';
import { useNavigate } from 'react-router-dom';

const defaultProps = {
	color: 'secondary' as AppColor,
	children: <NotificationsNoneIcon />,
};

const HeaderNotificationsMenu: FC = () => {
	const navigate = useNavigate();

	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLButtonElement>(null);

	const { user } = React.useContext(UserContext);

	const { dispatch: dispatchNotification } = React.useContext(NotificationContext);

	const [postsSnapshot, , error] = useCollectionData(
		query(
			collection(db, `users/${user?.uid}/notifications`),
			limit(10),
			orderBy('createdAt', 'desc'),
		).withConverter(userNotificationConverter),
	);

	if (error) {
		console.error(error);
		dispatchNotification({
			type: 'notification/SHOW_ERROR',
			payload: {
				message: error?.message ?? 'Unknown error',
			},
		});
	}

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: MouseEvent | TouchEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleListKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		}
	};

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = React.useRef(open);
	React.useEffect(() => {
		if (prevOpen.current && !open && anchorRef.current) {
			anchorRef.current.focus();
		}

		prevOpen.current = open;
	}, [open]);

	const markNotificationSeen = async (postId: string) => {
		try {
			await updateDoc(doc(db, `users/${user?.uid}/notifications/${postId}`), { seen: true });
		} catch (error: any) {
			console.error(error);
		}
	};

	const handleClick = (item: UserNotificationModel) => {
		markNotificationSeen(item.id);

		if (item.link) {
			navigate(item.link);
		}
	};

	return (
		<Box>
			<IconButton
				ref={anchorRef}
				aria-controls={open ? 'user-menu' : undefined}
				aria-haspopup="true"
				onClick={handleToggle}
			>
				<Badge badgeContent={user.notificationsNumber ?? 0} max={99} {...defaultProps} />
			</IconButton>
			<Popper
				id="user-menu"
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				placement="bottom-end"
			>
				{({ TransitionProps }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: 'right top',
						}}
					>
						<Paper style={{ maxWidth: 500 }}>
							<ClickAwayListener onClickAway={handleClose}>
								{postsSnapshot?.length ? (
									<List
										aria-label="notifications list"
										id="menu-list-grow"
										onKeyDown={handleListKeyDown}
										dense
									>
										{postsSnapshot?.map((item) => (
											<ListItemButton
												key={item.id}
												sx={[
													{ fontSize: 14 },
													!item.seen && {
														position: 'relative',
														paddingLeft: '20px',

														'&::before': {
															content: '""',
															position: 'absolute',
															display: 'block',
															left: 7,
															top: '50%',
															marginTop: '-3px',
															width: 6,
															height: 6,
															borderRadius: '50%',
															backgroundColor: 'primary.main',
														},
													},
												]}
												onClick={() => handleClick(item)}
											>
												{item.icon && (
													<ListItemAvatar>
														<Avatar src={item.icon} />
													</ListItemAvatar>
												)}
												<ListItemText
													primary={item.title}
													secondary={
														<span style={{ fontSize: 12 }}>
															{moment(
																item.createdAt.toMillis(),
															).fromNow()}
														</span>
													}
												/>
											</ListItemButton>
										))}
									</List>
								) : (
									<Box p={2}>
										<Typography variant="body2" color="textSecondary">
											Empty.
										</Typography>
									</Box>
								)}
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</Box>
	);
};

export default HeaderNotificationsMenu;
